// Read more: the pages pasted in at the back of a realm's chronicle. Its whole summary, its curiosities (facts
// that belong to no one year) and its further reading (books, talks, podcasts, places to visit, each with a
// line on why it is worth the time). Opened from inside an age, that age's own notes come first. The panel is
// a sheet laid beside the scroll at its full size; it is written once when it opens and never reflows after.

import type { Age, Realm } from "@hoee/chart";
import { fmtYear } from "@hoee/chart";
import type { Chronicle, Fact, Source, SourceType } from "@hoee/schema";

/** The shelves sources are grouped on, and what each type is called on its label. */
const SHELVES: { head: string; types: SourceType[] }[] = [
  { head: "To read", types: ["book", "primary", "article", "website"] },
  { head: "To watch", types: ["video", "lecture"] },
  { head: "To listen to", types: ["podcast"] },
  { head: "To visit", types: ["museum"] },
];
const LABEL: Record<SourceType, string> = {
  book: "Book",
  primary: "In their own words",
  article: "Article",
  website: "On the web",
  video: "Video",
  lecture: "Lecture",
  podcast: "Podcast",
  museum: "Museum",
};

const wikiUrl = (t: string) => `https://en.wikipedia.org/wiki/${encodeURIComponent(t.replace(/ /g, "_"))}`;

/** Where a source can be found: its own address, else the book on Open Library by ISBN, else a search there. */
export function sourceHref(src: Source): string {
  if (src.url) return src.url;
  if (src.isbn) return `https://openlibrary.org/isbn/${src.isbn.replace(/-/g, "")}`;
  return `https://openlibrary.org/search?q=${encodeURIComponent(`${src.title} ${src.by}`)}`;
}

/** Where a link goes, said in words for those who cannot see the arrow: the site's own name. */
function siteOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

const ROMAN: [number, string][] = [
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];
const roman = (n: number) =>
  ROMAN.reduce((out, [v, r]) => {
    while (n >= v) {
      out += r;
      n -= v;
    }
    return out;
  }, "");

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string) => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text) node.textContent = text;
  return node;
};
function link(cls: string, href: string, text: string): HTMLAnchorElement {
  const a = el("a", cls, text);
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener";
  return a;
}

export class ReadingPanel {
  readonly el: HTMLElement;
  private readonly body: HTMLElement;
  private readonly heading: HTMLElement;
  private readonly span: HTMLElement;
  private readonly onClose: () => void;
  /** The realm and age the panel was written for. */
  about: { realm: Realm; age: Age | null } | null = null;
  private returnFocus: HTMLElement | null = null;

  constructor(onClose: () => void) {
    this.onClose = onClose;
    this.el = el("aside", "reading");
    this.el.id = "reading";
    this.el.hidden = true;
    this.el.setAttribute("aria-labelledby", "reading-title");
    const head = el("header", "reading-head");
    const eyebrow = el("p", "reading-eyebrow", "Curiosities & further reading");
    this.heading = el("h2", "reading-title");
    this.heading.id = "reading-title";
    this.heading.tabIndex = -1;
    this.span = el("p", "reading-span");
    const close = el("button", "reading-close", "×");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.close());
    head.append(close, eyebrow, this.heading, this.span);
    this.body = el("div", "reading-body");
    this.el.append(head, this.body);
    this.el.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen(): boolean {
    return this.about != null;
  }

  /** Writes the panel for a realm (and the age you are in, if any) and lays it beside the scroll. */
  open(realm: Realm, chronicle: Chronicle, trail: readonly Age[], span: string): void {
    const here = trail.at(-1) ?? null;
    this.about = { realm, age: here };
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.heading.textContent = realm.name;
    this.span.textContent = span;

    const parts: HTMLElement[] = [el("p", "reading-summary", chronicle.summary)];
    // The ages you are in, deepest first: theirs are the notes closest to what is on screen.
    const seen = new Set<string>();
    for (const age of [...trail].reverse()) {
      const { period } = age;
      const facts = period.facts ?? [];
      const sources = (period.sources ?? []).filter((s) => !seen.has(s.title));
      if (!facts.length && !sources.length) continue;
      for (const s of sources) seen.add(s.title);
      const section = el("section", "reading-age");
      section.setAttribute("aria-label", period.name);
      const h = el("h3", "reading-age-head");
      h.append(
        el("span", "reading-age-in", "In this age"),
        el("span", "reading-age-name", period.name),
        el("span", "reading-age-years", `${fmtYear(period.from)} – ${fmtYear(period.to)}`),
      );
      section.append(h);
      if (facts.length) section.append(this.curiosities(facts, "h4"));
      if (sources.length) section.append(this.shelves(sources, "h4"));
      parts.push(section);
    }
    let n = 0;
    const part = (title: string, content: HTMLElement) => {
      const section = el("section", "reading-part");
      const h = el("h3", "reading-part-head");
      const num = el("span", "reading-num", roman(++n));
      num.setAttribute("aria-hidden", "true");
      h.append(num, el("span", "", title));
      section.append(h, content);
      parts.push(section);
    };
    if (chronicle.facts?.length) part("Curiosities", this.curiosities(chronicle.facts, "h4"));
    const sources = (chronicle.sources ?? []).filter((s) => !seen.has(s.title));
    if (sources.length) part("Further reading", this.shelves(sources, "h4"));
    const q = encodeURIComponent(realm.name);
    const further = el("p", "reading-further", "Search further: ");
    [
      ["Wikipedia", `https://en.wikipedia.org/w/index.php?search=${q}`],
      ["Open Library", `https://openlibrary.org/search?q=${q}`],
      ["YouTube", `https://www.youtube.com/results?search_query=${q}%20history`],
    ].forEach(([name, href], i) => {
      if (i) further.append(" · ");
      further.append(link("", href ?? "", name ?? ""));
    });
    parts.push(further);

    this.body.replaceChildren(...parts);
    this.body.scrollTop = 0;
    this.el.hidden = false;
    // Laid down at its full size, then slid into view: nothing inside it moves afterwards.
    requestAnimationFrame(() => {
      this.el.classList.add("open");
      this.heading.focus({ preventScroll: true });
    });
  }

  close(): void {
    if (!this.about) return;
    this.about = null;
    this.el.classList.remove("open");
    setTimeout(() => {
      if (!this.about) this.el.hidden = true;
    }, 260);
    if (this.el.contains(document.activeElement)) this.returnFocus?.focus({ preventScroll: true });
    this.onClose();
  }

  private curiosities(facts: readonly Fact[], tag: "h4"): HTMLElement {
    const list = el("ol", "curios");
    facts.forEach((f, i) => {
      const li = el("li", "curio");
      const n = el("span", "curio-n", roman(i + 1));
      n.setAttribute("aria-hidden", "true");
      li.append(n, el(tag, "curio-title", f.title), el("p", "curio-text", f.text));
      if (f.wiki) {
        const a = link("curio-link", wikiUrl(f.wiki), "Wikipedia ↗");
        a.setAttribute("aria-label", `${f.title} on Wikipedia (opens in a new tab)`);
        li.append(a);
      }
      list.append(li);
    });
    return list;
  }

  private shelves(sources: readonly Source[], tag: "h4"): HTMLElement {
    const wrap = el("div", "shelves");
    for (const shelf of SHELVES) {
      const here = sources.filter((s) => shelf.types.includes(s.type));
      if (!here.length) continue;
      wrap.append(el(tag, "shelf-head", shelf.head));
      const list = el("ul", "shelf");
      for (const src of here) {
        const li = el("li", `src src-${src.type}`);
        const href = sourceHref(src);
        const title = link("src-title", href, src.title);
        const where = src.url ? siteOf(href) : "Open Library";
        title.setAttribute("aria-label", `${src.title}, ${LABEL[src.type]} (${where}, opens in a new tab)`);
        const by = el("p", "src-by");
        by.append(el("span", "src-type", LABEL[src.type]), [src.by, src.year].filter(Boolean).join(", "));
        li.append(title, by, el("p", "src-note", src.note));
        list.append(li);
      }
      wrap.append(list);
    }
    return wrap;
  }
}
